import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AdvancedSearchPreviewCard } from 'src/app/models/advanced-search';

@Component({
  selector: 'app-advanced-search-thumb-card',
  templateUrl: './advanced-search-thumb-card.component.html',
  styleUrls: ['./advanced-search-thumb-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedSearchThumbCardComponent {
  @Input() card!: AdvancedSearchPreviewCard;
  @Input() actionsEnabled: boolean = false;
  @Input() isPinned: boolean = false;
  @Input() landscapeLoading: boolean = false;
  // Selection (plain search → save-to-collection). When enabled, a checkbox is
  // shown top-right; clicking it toggles selection, clicking elsewhere opens the
  // preview. selectable=false renders a disabled checkbox (can't be saved).
  @Input() selectionEnabled: boolean = false;
  @Input() selectable: boolean = true;
  @Input() selected: boolean = false;

  @Output() previewOpened = new EventEmitter<string>();
  @Output() retryRequested = new EventEmitter<string>();
  @Output() pinToggled = new EventEmitter<string>();
  @Output() deleteRequested = new EventEmitter<string>();
  @Output() selectionToggled = new EventEmitter<string>();

  public get isSuccess(): boolean {
    return this.card?.status === 'success' && !!this.thumbnailImageSrc;
  }

  public get showSelectionCheck(): boolean {
    return this.selectionEnabled && this.isSuccess;
  }

  public get thumbnailImageSrc(): string {
    return this.card?.thumbnailImageDataUrl || this.card?.imageDataUrl || '';
  }

  public get isLoading(): boolean {
    return this.card?.status === 'loading';
  }

  public get isPending(): boolean {
    return this.card?.status === 'pending';
  }

  public get isError(): boolean {
    return this.card?.status === 'error';
  }

  public get isContentProhibited(): boolean {
    return this.card?.status === 'error' && this.card?.errorCode === 'content_prohibited';
  }

  public get isPlanningFailed(): boolean {
    return this.card?.status === 'error' && this.card?.errorCode === 'planning_failed';
  }

  public get canRetry(): boolean {
    return this.isError && !this.isContentProhibited && !this.isPlanningFailed;
  }

  public get statusText(): string {
    if (this.landscapeLoading) {
      return 'Preparing pair preview';
    }

    if (this.isContentProhibited) {
      return 'Rephrase search';
    }

    if (this.isPlanningFailed) {
      return 'Planning failed';
    }

    if (this.isError) {
      return 'Retry available';
    }

    if (this.isSuccess) {
      return 'Open preview';
    }

    return '';
  }

  public onCardClick(): void {
    if (!this.isSuccess || !this.card?.id) {
      return;
    }

    this.previewOpened.emit(this.card.id);
  }

  public onSelectionToggle(event: Event): void {
    event.stopPropagation();

    if (!this.selectable || !this.card?.id) {
      return;
    }

    this.selectionToggled.emit(this.card.id);
  }

  public onRetryClick(event: Event): void {
    event.stopPropagation();

    if (!this.card?.id) {
      return;
    }

    this.retryRequested.emit(this.card.id);
  }

  public onPinToggleClick(event: Event): void {
    event.stopPropagation();

    if (!this.actionsEnabled || !this.isSuccess || !this.card?.id) {
      return;
    }

    this.pinToggled.emit(this.card.id);
  }

  public onDeleteClick(event: Event): void {
    event.stopPropagation();

    if (!this.actionsEnabled || !this.card?.id) {
      return;
    }

    this.deleteRequested.emit(this.card.id);
  }
}
